import { type ReactElement, useState } from 'react';
import { refreshSession } from '@/entities/user';
import { getErrorMessage } from '@/shared/lib';
import { BlockButton } from '@/shared/ui';
import { useResendVerification } from '../model/use-resend-verification';

interface EmailVerificationStepProps {
  /** 인증 완료 확인 시 계정 상태를 다시 불러오는 콜백. */
  readonly onRefresh: () => Promise<void>;
}

/** 이메일 인증 대기 화면 — 재발송과 상태 갱신을 제공한다. */
export const EmailVerificationStep = ({ onRefresh }: EmailVerificationStepProps): ReactElement => {
  const resend = useResendVerification();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const handleRefresh = async (): Promise<void> => {
    setIsRefreshing(true);
    try {
      // 액세스 토큰의 status 가 옛값일 수 있어 먼저 세션을 재발급받는다.
      await refreshSession();
      await onRefresh();
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <p className="border-l-2 border-brand-lime bg-muted/70 px-4 py-3 text-[15px] leading-relaxed">
        가입하신 이메일로 인증 링크를 보냈습니다. 메일의 링크를 열어 인증을 완료해 주세요.
      </p>

      {resend.isError ? (
        <p
          role="alert"
          className="border-l-2 border-destructive bg-destructive/5 px-4 py-3 text-sm text-destructive"
        >
          {getErrorMessage(resend.error, '재발송에 실패했습니다.')}
        </p>
      ) : null}
      {resend.isSuccess ? (
        <p className="text-sm text-muted-foreground">인증 메일을 다시 보냈습니다.</p>
      ) : null}

      <div className="mt-3 flex flex-col gap-2.5">
        <BlockButton type="button" isLoading={isRefreshing} onClick={() => void handleRefresh()}>
          인증 완료
        </BlockButton>
        <BlockButton
          type="button"
          variant="outline"
          isLoading={resend.isPending}
          onClick={() => resend.mutate()}
        >
          인증 메일 재발송
        </BlockButton>
      </div>
    </div>
  );
};
